// App.tsx
import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { useTenant } from "./contexts/TenantContext";
import { useAuth } from "./contexts/AuthContext";
import Login from "./components/Login";
import DomainNotApproved from "./components/DomainNotApproved";
import { TailwindTest } from "./components/TailwindTest";
import WorkflowStudio from "./components/studio/WorkflowStudio";
import { WorkflowList } from "./pages/WorkflowManager/WorkflowList";
import { WorkflowUpload } from "./pages/WorkflowManager/WorkflowUpload";
import { WorkflowView } from "./pages/WorkflowManager/WorkflowView";
import { PresentationEditor } from "./pages/WorkflowManager/PresentationEditor";
import { AcceptInvite } from "./pages/AcceptInvite";
import { AssetLibrary } from "./pages/AssetManager";
import { ProductLibrary } from "./pages/ProductManager";
import { LocationLibrary } from "./pages/LocationManager";
import AdminPanel from "./components/admin/AdminPanel";
import TenantManagementBanner from "./components/admin/TenantManagementBanner";
import { usePlatformAdmin } from "./hooks/usePlatformAdmin";
import { getApiBaseUrl } from "./config/environment";

function App() {
  // Get auth context for access token
  const { user, accessToken, isLoading: authLoading } = useAuth();

  // Get tenant info which includes user info
  const { currentTenant, currentUser, isLoading: tenantLoading, error: tenantError, activeTenant, isManagingOtherTenant } = useTenant();

  // Check if user is platform admin
  const { isPlatformAdmin } = usePlatformAdmin();

  // Check for special test mode via URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  const showTailwindTest = urlParams.get('test') === 'tailwind';

  // If test mode, show test page immediately
  if (showTailwindTest) {
    return <TailwindTest />;
  }

  // Show loading state while auth/tenant are initializing
  if (authLoading || tenantLoading) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        background: "#f7f9fb",
        gap: 16
      }}>
        <Spinner />
        <div style={{ color: "#666", fontSize: 14 }}>Loading...</div>
      </div>
    );
  }

  // Check if this is an invite acceptance flow
  const urlParams2 = new URLSearchParams(window.location.search);
  const hasInviteToken = urlParams2.get('token') || urlParams2.get('invite');

  if (hasInviteToken) {
    return (
      <Router>
        <Routes>
          <Route path="*" element={<AcceptInvite />} />
        </Routes>
      </Router>
    );
  }

  // Show login screen if not authenticated
  if (!user) {
    return <Login />;
  }

  // Check for domain not approved error
  if (tenantError && tenantError.startsWith('DOMAIN_NOT_APPROVED:')) {
    const message = tenantError.replace('DOMAIN_NOT_APPROVED: ', '');
    return <DomainNotApproved
      message={message}
      onRequestAccess={() => {
        // Optional: implement request access functionality
        alert('Request access functionality - to be implemented');
      }}
    />;
  }

  // Check for invite required error (invite-only mode)
  if (tenantError && tenantError.startsWith('INVITE_REQUIRED:')) {
    const message = tenantError.replace('INVITE_REQUIRED: ', '');
    return <DomainNotApproved
      message={message}
      onRequestAccess={() => {
        // Optional: implement request access functionality
        alert('Request access functionality - to be implemented');
      }}
    />;
  }

  // Check for invalid invite error
  if (tenantError && tenantError.startsWith('INVALID_INVITE:')) {
    const message = tenantError.replace('INVALID_INVITE: ', '');
    return <DomainNotApproved
      message={message}
      onRequestAccess={() => {
        // Optional: implement request access functionality
        alert('Request access functionality - to be implemented');
      }}
    />;
  }

  // Main application with routing
  if (user && accessToken && currentTenant) {
    const apiBaseUrl = getApiBaseUrl();

    // Use activeTenant for API calls (allows platform admin to manage other tenants)
    const tenantId = activeTenant?.tenantId || currentTenant.tenantId;
    const tenantName = activeTenant?.name || currentTenant.name || 'Creatives Combined';

    const userRole = currentUser?.role || 'member';
    const isOwner = userRole === 'owner' || isManagingOtherTenant; // Platform admin managing another tenant acts as owner

    return (
      <Router>
        {/* Tenant Management Banner (shows when platform admin is managing another tenant) */}
        <TenantManagementBanner />

        <Routes>
          {/* Main Workflow Studio */}
          <Route
            path="/"
            element={
              <WorkflowStudio
                accessToken={accessToken}
                userEmail={user.email || 'user@example.com'}
                tenantName={tenantName}
                tenantId={tenantId}
              />
            }
          />

          {/* Workflow Manager Routes (Owner or Platform Admin) */}
          {(isOwner || isPlatformAdmin) && (
            <>
              <Route
                path="/workflows"
                element={
                  <WorkflowList
                    tenantId={tenantId}
                    apiBaseUrl={apiBaseUrl}
                    authToken={accessToken}
                  />
                }
              />
              <Route
                path="/workflows/upload"
                element={
                  <WorkflowUpload
                    tenantId={tenantId}
                    apiBaseUrl={apiBaseUrl}
                    authToken={accessToken}
                  />
                }
              />
              <Route
                path="/workflows/:workflowId"
                element={
                  <WorkflowView
                    tenantId={tenantId}
                    apiBaseUrl={apiBaseUrl}
                    authToken={accessToken}
                  />
                }
              />
              <Route
                path="/workflows/:workflowId/edit"
                element={
                  <PresentationEditor
                    tenantId={tenantId}
                    apiBaseUrl={apiBaseUrl}
                    authToken={accessToken}
                  />
                }
              />
              <Route
                path="/assets"
                element={
                  <AssetLibrary
                    tenantId={tenantId}
                    apiBaseUrl={apiBaseUrl}
                    authToken={accessToken}
                  />
                }
              />
              <Route
                path="/products"
                element={
                  <ProductLibrary
                    tenantId={tenantId}
                    apiBaseUrl={apiBaseUrl}
                    authToken={accessToken}
                  />
                }
              />
              <Route
                path="/locations"
                element={
                  <LocationLibrary
                    tenantId={tenantId}
                    apiBaseUrl={apiBaseUrl}
                    authToken={accessToken}
                  />
                }
              />
            </>
          )}

          {/* Admin Panel Route (Platform Admin Only) */}
          {isPlatformAdmin && (
            <Route
              path="/admin"
              element={
                <AdminPanel
                  accessToken={accessToken}
                  userEmail={user.email || 'user@example.com'}
                  tenantName={tenantName}
                  tenantId={tenantId}
                />
              }
            />
          )}

          {/* Redirect unknown routes to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    );
  }

  // Fallback (should never reach here)
  return null;
}

function Spinner() {
  return (
    <div
      style={{
        width: 24,
        height: 24,
        border: "3px solid #ddd",
        borderTopColor: "#4b8bf4",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
      }}
    >
      <style>
        {`@keyframes spin { from { transform: rotate(0turn); } to { transform: rotate(1turn); } }`}
      </style>
    </div>
  );
}

export default App;
